import { z } from "zod";

// --- Order item as stored in tostore.tabledata.orders[key] ---
//
// Schema is the source of truth. Use `TableOrderItemSchema.parse(...)`
// to validate at the JSONB boundary (see table-order-adapters.ts).
// Unknown keys are stripped by default — the schema's the contract.

export const TableOrderItemSchema = z.object({
  sno: z.number(),
  materialNumber: z.string(),
  material: z.string(),
  description: z.string(),
  qty: z.number(),
  quantity: z.number(),
  mrp: z.number(),
  pourMrp: z.number(),
  value: z.number(),
  stock: z.number(),
  currentStock: z.number(),
  currentMl: z.number(),
  invoiceMl: z.number(),
  pourSize: z.number(),
  prepared: z.boolean(),
  preparedCount: z.number(),
  pourEnabled: z.enum(["Y", "N"]),
  pourType: z.string(),
  pourMaterialNumber: z.string(),
  pourDescription: z.string(),
  category: z.string(),
  subCategory: z.string(),
  materialType: z.string(),
  brandName: z.string(),
  brandItem: z.string(),
  hsnCode: z.string(),
  cess: z.number(),
  additionalCess: z.number(),
  purchasePrice: z.number(),
  weightedAveragePrice: z.number(),
  packageSize: z.string(),
  packageType: z.string(),
  taxRate: z.number(),
  aroed: z.number(),
  grossAmount: z.number(),
  discountAmount: z.number(),
  taxAmount: z.number(),
  cessAmount: z.number(),
  additionalCessAmount: z.number(),
  netAmount: z.number(),
  extraDiscount: z.number(),
  discountFlag: z.string(),
  discountType: z.string(),
  discountQuantity: z.number(),
  discountPercent: z.number(),
  offer: z.string(),
  offerType: z.number(),
  offerCategory: z.string(),
  rowid: z.number().optional(),
});

export type TableOrderItem = z.infer<typeof TableOrderItemSchema>;

// --- Bill data per table+bill ---

export interface TableOrderBillData {
  orders: Record<string, TableOrderItem>;
  waiter: string;
  billPrinted: boolean;
  beginprint: boolean;
  // Set by the waiter app when the customer asks for the bill.
  // POS shows this as a "Requested" status until the bill is printed.
  billRequested?: boolean;
  // Set by the waiter app when the waiter asks the POS to fire a KOT.
  // POS shows this as a "KOT Requested" status until the next KOT print
  // for the bill clears it.
  kotRequested?: boolean;
  total: number;
  discount: number;
  is_nocharge_bill: boolean;
  split: Record<string, number>;
  custcode?: string;
  custname?: string;
  auditId?: string;
  docid?: string;
}

// --- All tables response from readTableOrderStore ---

export type TableOrderStoreData = Record<
  string,
  Record<string, TableOrderBillData>
>;

// --- Table status derived for UI ---

export type TableStatus =
  | "vacant"
  | "occupied"
  | "ordered"
  | "preparing"
  | "ready"
  | "kotRequested"
  | "requested"
  | "billed";

// --- Generic v2 response wrapper ---

export interface TableOrderResponse<T> {
  message: string;
  data: T;
}

// --- API request/response types ---

export interface ReadTableOrderStoreRequest {
  shopId: string;
  table?: string;
  bill?: string;
  userId?: string;
}

export type ReadTableOrderStoreResponse = TableOrderResponse<
  | TableOrderStoreData
  | TableOrderBillData
  | Record<string, TableOrderBillData>
  | Record<string, never>
>;

export interface WriteTableOrderStoreRequest {
  shopId: string;
  table: string;
  bill: string;
  data: TableOrderBillData;
  userId: string;
}

export interface WriteTableOrderStoreResponseData {
  table: string;
  bill: string;
  tableData: Record<string, TableOrderBillData>;
}

export type WriteTableOrderStoreResponse =
  TableOrderResponse<WriteTableOrderStoreResponseData>;

export interface DeleteTableOrderStoreRequest {
  shopId: string;
  table: string;
  bill?: string;
  userId: string;
}

export interface DeleteTableOrderStoreResponseData {
  table: string;
  tableData: Record<string, TableOrderBillData>;
}

export type DeleteTableOrderStoreResponse =
  TableOrderResponse<DeleteTableOrderStoreResponseData>;

export interface CheckPasswordRequest {
  shopId: string;
  password: string;
  isNoChargePassword?: boolean;
  deleteMaterial?: string;
}

export type CheckPasswordResponse = TableOrderResponse<string>;

export interface TableOrderInvoiceHeader {
  counterName: string;
  grossAmount: string;
  taxAmount: string;
  totalQuantity: string;
  netAmount: string;
  discountAmount: string;
  paymentMode: string;
  customerCode?: string;
  customerName?: string;
  extraDiscountPercent?: number;
  loyaltyPoints?: string;
  loyaltyDiscountAmount?: string;
  extraLoyaltyDiscountAmount?: string;
  cessAmount?: string;
  additionalCessAmount?: string;
  cashCollected?: string;
  balanceReturned?: string;
  kotName?: string;
  laneId?: number;
}

export interface TableOrderInvoiceItem {
  rowId: number;
  description: string;
  quantity: number;
  mrp: number;
  grossAmount: number;
  netAmount: number;
  materialNumber: string;
  offer?: string;
  currentStock?: number;
  discountFlag?: string;
  discountType?: string;
  discountQuantity?: number;
  discountPercent?: number;
  extraDiscount?: number;
  discountAmount?: number;
  taxRate?: number;
  taxAmount?: number;
  cess?: number;
  additionalCess?: number;
  cessAmount?: number;
  additionalCessAmount?: number;
  category?: string;
  subCategory?: string;
  materialType?: string;
  brandName?: string;
  brandItem?: string;
  packageType?: string;
  packageSize?: number;
  hsnCode?: string;
  purchasePrice?: number;
  weightedAveragePrice?: number;
  offerType?: number;
  offerCategory?: string;
  aroed?: number;
  // Pour-sale fields. For bottle sales all four are zero/empty; for
  // pour sales quantity is 0 and these carry the peg count + parent.
  // `invoiceMl` is peg count (not ml) despite the name — wobpos legacy.
  invoiceMl?: number;
  pourMaterialNumber?: string;
  pourSize?: number;
  pourDescription?: string;
}

export interface TableOrderSaveInvoiceEntry {
  userType: string;
  // Split-bill group key (e.g. "alc", "nalc", "food"). Backend echoes
  // this back as the key in `return_data`, letting the client pair
  // docids with the original item groups. Omit for non-split bills.
  index?: string;
  isNochargeBill?: boolean;
  header: TableOrderInvoiceHeader;
  items: TableOrderInvoiceItem[];
}

export interface TableOrderSaveInvoiceRequest {
  shopId: string;
  posUniqueKey: string;
  counterType?: string;
  saveDate?: string;
  invoices: TableOrderSaveInvoiceEntry[];
}

export interface TableOrderSaveInvoiceResponse {
  message: string;
  data: {
    docid?: string;
    return_data?: Record<string, string>;
  };
}

export interface OpenBottleRequest {
  shopId: string;
  materialNumber: string;
  packageSize: string;
  quantity: string;
  laneId: number;
  userName: string;
  materialType?: string;
  category?: string;
  subCategory?: string;
  brandName?: string;
  brandItem?: string;
}

export type OpenBottleResponse = TableOrderResponse<Record<string, unknown>>;

export interface ValidateTableOrderStockRequest {
  shopId: string;
  materials: string[];
}

export interface ValidateTableOrderStockItem {
  materialNumber: string;
  currentStock: number;
  currentMl: number;
}

export interface ValidateTableOrderStockResponse {
  message: string;
  data: ValidateTableOrderStockItem[];
}

export type TableOrderEventCode =
  | "WAIT"
  | "WCH"
  | "ADD"
  | "Q-CH"
  | "DEL"
  | "KOT"
  | "KOT-RE"
  | "PRNT"
  | "UPDP"
  | "MV"
  | "ITM-MV"
  | "SPLIT"
  | "MERGE"
  | "VOID"
  | "DREF"
  | "TREF"
  | "CUS"
  | "BILL"
  | "BILL-REQ"
  | "BILL-REQ-X"
  | "KOT-REQ"
  | "KOT-REQ-X"
  | "DISC"
  | "NCB"
  | "OPEN-B"
  | "MOD"
  | "PASS";

export interface WriteTableOrderLogRequest {
  eventCode: TableOrderEventCode;
  tableName?: string;
  bill?: string;
  docid?: string;
  actorName?: string;
  waiterName?: string;
  payload?: Record<string, unknown>;
}

export interface WriteTableOrderLogResponse {
  message: string;
  data: TableOrderAuditEntry;
}

// Wire shape for the /realtime `table-order:log` event and for entries
// returned by the broadcast endpoint. Server stamps actorType/actorId/at
// on broadcast; receivers treat it as authoritative.
export interface TableOrderLogEvent {
  tableName: string | null;
  bill: string | null;
  docid: string | null;
  eventCode: TableOrderEventCode;
  actorType: "pos" | "waiter";
  actorId: string;
  actorName: string;
  waiterName: string | null;
  payload: Record<string, unknown> | null;
  at: string;
}

export interface BroadcastTableOrderLogRequest {
  eventCode: TableOrderEventCode;
  tableName?: string;
  bill?: string;
  docid?: string;
  actorName?: string;
  waiterName?: string;
  payload?: Record<string, unknown>;
}

export interface WriteTableOrderLogBatchRequest {
  docid: string;
  events: BroadcastTableOrderLogRequest[];
}

export interface TableOrderAuditEntry {
  id: number;
  auditId: string | null;
  tableName: string | null;
  bill: string | null;
  docid: string | null;
  eventCode: TableOrderEventCode;
  actorType: "waiter" | "pos";
  actorId: string | null;
  actorName: string | null;
  waiterName: string | null;
  payload: Record<string, unknown> | null;
  crdt: string;
}

export interface FetchTableOrderAuditRequest {
  tableName?: string;
  bill?: string;
  auditId?: string;
  docid?: string;
  eventCode?: TableOrderEventCode;
  actorType?: "waiter" | "pos";
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface FetchTableOrderAuditResponse {
  message: string;
  data: {
    entries: TableOrderAuditEntry[];
    total: number;
    page: number;
    limit: number;
  };
}

// --- Update Price / Stock ---

// Matches the backend response shape (camelCase, descriptive names)
// from `GET /table-order/food-materials`. The legacy short DB names
// (matnr, matdesc, curstock, etc.) are intentionally not used —
// per pos-rest-api/CLAUDE.md the API contract uses descriptive
// English names everywhere.
export interface UpdatePriceMaterial {
  materialNumber: string;
  description: string;
  mrp: string | number;
  purchasePrice: string | number;
  category: string;
  subCategory: string;
  materialType: string;
  categoryNumber: string;
  subCategoryNumber: string;
  currentStock: string | number;
}

export type GetUpdatePriceMaterialResponse = TableOrderResponse<
  UpdatePriceMaterial[]
>;

export interface UpdateNewMrpRequest {
  shopId: string;
  materialNumber: string;
  mrp: number;
  userType: string;
}

export type UpdateNewMrpResponse = TableOrderResponse<string>;

export interface UpdateStockRequest {
  shopId: string;
  materialNumber: string;
  currentStock: number;
}

export type UpdateStockResponse = TableOrderResponse<string>;
